import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

export interface Brand {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'inactive';
  category?: string;
  type?: string;
  website?: string | null;
  logoUrl?: string;
  description?: string;
  tags?: string[];
  aliases?: string[];
  country?: string | null;
  categories?: string[];
  /** Demo brand: hidden from every store and vendor brand list (MOT-338). */
  adminOnly?: boolean;
  /** The brand allows sale pricing only inside vendor-approved sale windows (MOT-388). */
  saleWindowsRequired?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface BrandListResponse {
  brands: Brand[];
  totals: {
    total: number;
    active: number;
    inactive: number;
  };
}

class BrandManagementApi {
  private async request(endpoint: string, options: RequestInit = {}) {
    const response = await makeApiCall(endpoint, {
      ...options,
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async listBrands(params: { status?: string; search?: string; category?: string } = {}): Promise<BrandListResponse> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.category) searchParams.append('category', params.category);

    const result = await this.request(`/admin/brands?${searchParams.toString()}`);
    return result.data;
  }

  async createBrand(data: Partial<Brand>): Promise<Brand> {
    const result = await this.request('/admin/brands', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  async updateBrand(brandId: string, data: Partial<Brand>): Promise<Brand> {
    const result = await this.request(`/admin/brands/${brandId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  async setBrandStatus(brandId: string, status: 'active' | 'inactive'): Promise<Brand> {
    const result = await this.request(`/admin/brands/${brandId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return result.data;
  }

  async syncFromVendors(): Promise<{
    total: number;
    created: number;
    reactivated: number;
    updated: number;
  }> {
    const result = await this.request('/admin/brands/sync-from-vendors', {
      method: 'POST',
    });
    return result.data;
  }

  async syncFromOrganizations(): Promise<{
    total: number;
    created: number;
    updated: number;
  }> {
    const result = await this.request('/admin/brands/sync-from-organizations', {
      method: 'POST',
    });
    return result.data;
  }
}

export const brandManagementApi = new BrandManagementApi();

